import React, { useState, useRef, useCallback } from 'react'
import { Upload, Download, Undo, Redo, Search, Database, Table, AlertCircle, Save, Plus } from 'lucide-react'
import { DatabaseData, DatabaseType, ColumnInfo, SearchResult } from '../../types/database'
import { detectDatabaseType, formatFileSize } from '@utils/file'
import { DATABASE_TYPE_LABELS } from '@constants/database'
import { DatabaseService } from '@services/databaseService'
import { EditableTable } from './EditableTable'
import { useEditHistory } from '@hooks/useEditHistory'
import { searchInDatabase, debounce } from '@utils/search'
import { SearchResults } from '../ui/SearchResults'
import { SearchOptions } from '../ui/SearchOptions'
import { ThemeToggle } from '../ui/ThemeToggle'

export const DatabaseViewer: React.FC = () => {
  const [databaseData, setDatabaseData] = useState<DatabaseData | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [showSearchResults, setShowSearchResults] = useState(false)
  const [searchOptions, setSearchOptions] = useState({
    caseSensitive: false,
    matchType: 'partial' as 'exact' | 'partial' | 'regex'
  })
  const [selectedTable, setSelectedTable] = useState<string>('')
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  
  const fileInputRef = useRef<HTMLInputElement>(null)
  const databaseService = DatabaseService.getInstance()
  const { undo, redo, canUndo, canRedo, editCount, clearHistory } = useEditHistory()

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setIsLoading(true)
    setError(null)
    setSelectedFile(file)
    setHasUnsavedChanges(false)
    clearHistory()

    try {
      const data = await databaseService.parseDatabase(file)
      setDatabaseData(data)
      if (data.info.tables.length > 0) {
        setSelectedTable(data.info.tables[0].name)
      }
    } catch (error) {
      console.error('ファイルの読み込みに失敗しました:', error)
      setError(error instanceof Error ? error.message : '不明なエラーが発生しました')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDataChange = (tableName: string, rowIndex: number, columnName: string, newValue: any) => {
    if (!databaseData) return

    setDatabaseData(prev => {
      if (!prev) return prev

      const newData = { ...prev }
      if (newData.tables[tableName]?.rows[rowIndex]) {
        newData.tables[tableName].rows[rowIndex] = {
          ...newData.tables[tableName].rows[rowIndex],
          [columnName]: newValue
        }
      }

      return newData
    })

    setHasUnsavedChanges(true)
  }

  const handleColumnAdd = (tableName: string, columnName: string, columnType: string) => {
    if (!databaseData) return

    setDatabaseData(prev => {
      if (!prev) return prev

      const newData = { ...prev }
      const table = newData.tables[tableName]
      
      if (table) {
        // 新しい列を追加
        const newColumn: ColumnInfo = {
          name: columnName,
          type: columnType,
          nullable: true,
          primaryKey: false
        }
        
        table.columns.push(newColumn)
        
        // 既存の行に新しい列の値を追加
        table.rows.forEach(row => {
          row[columnName] = null
        })
        
        // テーブル情報も更新
        const tableInfo = newData.info.tables.find(t => t.name === tableName)
        if (tableInfo) {
          tableInfo.columns = table.columns
        }
      }

      return newData
    })

    setHasUnsavedChanges(true)
  }

  const handleColumnDelete = (tableName: string, columnName: string) => {
    if (!databaseData) return

    setDatabaseData(prev => {
      if (!prev) return prev

      const newData = { ...prev }
      const table = newData.tables[tableName]
      
      if (table) {
        // 列を削除
        table.columns = table.columns.filter(col => col.name !== columnName)
        
        // 既存の行から列の値を削除
        table.rows.forEach(row => {
          delete row[columnName]
        })
        
        // テーブル情報も更新
        const tableInfo = newData.info.tables.find(t => t.name === tableName)
        if (tableInfo) {
          tableInfo.columns = table.columns
        }
      }

      return newData
    })

    setHasUnsavedChanges(true)
  }

  const handleRowAdd = (tableName: string) => {
    if (!databaseData) return

    setDatabaseData(prev => {
      if (!prev) return prev

      const newData = { ...prev }
      const table = newData.tables[tableName]
      
      if (table) {
        // 新しい行を作成
        const newRow: any = {}
        table.columns.forEach(column => {
          newRow[column.name] = null
        })
        
        table.rows.push(newRow)
        
        // テーブル情報も更新
        const tableInfo = newData.info.tables.find(t => t.name === tableName)
        if (tableInfo) {
          tableInfo.rowCount = table.rows.length
        }
      }

      return newData
    })

    setHasUnsavedChanges(true)
  }

  const handleRowDelete = (tableName: string, rowIndex: number) => {
    if (!databaseData) return

    setDatabaseData(prev => {
      if (!prev) return prev

      const newData = { ...prev }
      const table = newData.tables[tableName]
      
      if (table) {
        // 行を削除
        table.rows.splice(rowIndex, 1)
        
        // テーブル情報も更新
        const tableInfo = newData.info.tables.find(t => t.name === tableName)
        if (tableInfo) {
          tableInfo.rowCount = table.rows.length
        }
      }

      return newData
    })

    setHasUnsavedChanges(true)
  }

  const handleDownload = async () => {
    if (!selectedFile || !databaseData) return
    
    try {
      const blob = await databaseService.exportDatabase(databaseData, selectedFile.name)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      // ファイル名を適切に設定
      const originalName = selectedFile.name
      const extension = originalName.split('.').pop()
      const baseName = originalName.replace(`.${extension}`, '')
      link.download = `${baseName}_edited.${extension}`
      
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      
      setHasUnsavedChanges(false)
    } catch (error) {
      console.error('ダウンロードに失敗しました:', error)
      setError(error instanceof Error ? error.message : 'ダウンロードに失敗しました')
    }
  }

  const handleUndo = () => {
    undo()
  }

  const handleRedo = () => {
    redo()
  }

  const performSearch = useCallback(() => {
    if (!databaseData || !searchQuery.trim()) {
      setSearchResults([])
      setShowSearchResults(false)
      return
    }

    const results = searchInDatabase(databaseData, searchQuery, searchOptions)
    setSearchResults(results)
    setShowSearchResults(true)
  }, [databaseData, searchQuery, searchOptions])

  const debouncedSearch = useCallback(
    debounce(performSearch, 300),
    [performSearch]
  )

  const handleSearch = () => {
    performSearch()
  }

  const handleSearchQueryChange = (query: string) => {
    setSearchQuery(query)
    if (query.trim()) {
      debouncedSearch()
    } else {
      setSearchResults([])
      setShowSearchResults(false)
    }
  }

  const handleSearchResultClick = (result: SearchResult) => {
    setSelectedTable(result.tableName)
    setShowSearchResults(false)
    // TODO: 該当するセルにスクロールする機能を追加
  }

  const supportedFormats = databaseService.getSupportedFormats()

  return (
    <div className="space-y-6">
            {/* ファイル選択セクション */}
      <div className="card">
        <div className="action-panel mb-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="btn btn-primary"
              disabled={isLoading}
            >
              <Upload className="icon-lg" />
              <span>ファイルを選択</span>
            </button>
            
            {selectedFile && (
              <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                <span className="badge badge-primary">ファイルサイズ: {formatFileSize(selectedFile.size)}</span>
                <span className="badge badge-success">編集済みセル: {editCount}個</span>
                {hasUnsavedChanges && (
                  <span className="badge badge-warning">未保存の変更があります</span>
                )}
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <ThemeToggle />
            <button
              onClick={handleUndo}
              disabled={!canUndo}
              className="btn btn-secondary"
              title="元に戻す"
            >
              <Undo className="icon" />
            </button>
            <button
              onClick={handleRedo}
              disabled={!canRedo}
              className="btn btn-secondary"
              title="やり直し"
            >
              <Redo className="icon" />
            </button>
            <button
              onClick={handleDownload}
              disabled={!selectedFile}
              className="btn btn-success"
            >
              <Save className="icon" />
              <span>編集済みファイルをダウンロード</span>
            </button>
          </div>
        </div>

        {/* 検索セクション */}
        <div className="action-panel">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 icon text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchQueryChange(e.target.value)}
                placeholder="テキストを検索..."
                className="input pl-10"
              />
            </div>
            <button 
              onClick={handleSearch}
              className="btn btn-success"
              disabled={!searchQuery.trim()}
            >
              <Search className="icon" />
              <span>検索</span>
            </button>
          </div>
          
          {searchQuery.trim() && (
            <SearchOptions
              caseSensitive={searchOptions.caseSensitive}
              matchType={searchOptions.matchType}
              onCaseSensitiveChange={(value) => setSearchOptions(prev => ({ ...prev, caseSensitive: value }))}
              onMatchTypeChange={(value) => setSearchOptions(prev => ({ ...prev, matchType: value }))}
            />
          )}
        </div>

        {/* サポートされている形式 */}
        <div className="action-panel">
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">サポートされている形式:</h3>
            <div className="flex flex-wrap gap-2">
              {supportedFormats.map((format) => (
                <span
                  key={format.type}
                  className="badge badge-primary"
                >
                  {format.description}
                </span>
              ))}
            </div>
          </div>
        </div>

                 <input
           ref={fileInputRef}
           type="file"
           onChange={handleFileSelect}
           accept=".db,.sqlite,.sqlite3,.csv,.tsv,.json,.mdb,.accdb,.sql,.dump,.backup"
           className="hidden"
         />
      </div>

      {/* エラー表示 */}
             {error && (
                  <div className="card border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/50">
            <div className="flex items-center space-x-2 text-red-700 dark:text-red-400">
              <AlertCircle className="icon-lg" />
              <span className="font-medium">エラー</span>
            </div>
            <p className="mt-1 text-red-600 dark:text-red-400">{error}</p>
          </div>
       )}

      {/* データベース情報 */}
      {databaseData && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
                         <div className="flex items-center space-x-2">
               <Database className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                 {DATABASE_TYPE_LABELS[databaseData.info.type]}
               </h2>
            </div>
                         <span className="text-sm text-gray-500 dark:text-gray-400">
               テーブル数: {databaseData.info.tables.length}
             </span>
          </div>

          {/* テーブル一覧 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {databaseData.info.tables.map((table) => (
              <div
                key={table.name}
                onClick={() => setSelectedTable(table.name)}
                className={`table-card ${
                  selectedTable === table.name ? 'selected' : ''
                }`}
              >
                <div className="flex items-center space-x-2 mb-2">
                  <Table className="icon text-gray-500 dark:text-gray-400" />
                  <span className="font-medium text-gray-900 dark:text-gray-100">{table.name}</span>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {table.rowCount} 行, {table.columns.length} 列
                </div>
              </div>
            ))}
          </div>

          {/* テーブルデータ表示 */}
          {selectedTable && databaseData.tables[selectedTable] && (
            <div className="mt-6">
                             <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">{selectedTable}</h3>
              <EditableTable
                tableName={selectedTable}
                columns={databaseData.tables[selectedTable].columns}
                rows={databaseData.tables[selectedTable].rows}
                onDataChange={handleDataChange}
                onColumnAdd={handleColumnAdd}
                onColumnDelete={handleColumnDelete}
                onRowAdd={handleRowAdd}
                onRowDelete={handleRowDelete}
              />
            </div>
          )}
        </div>
      )}

      {/* ローディング状態 */}
      {isLoading && (
                 <div className="card text-center py-8">
           <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="mt-2 text-gray-600 dark:text-gray-400">ファイルを読み込み中...</p>
         </div>
      )}

      {/* 検索結果モーダル */}
      <SearchResults
        results={searchResults}
        onResultClick={handleSearchResultClick}
        onClose={() => setShowSearchResults(false)}
        isVisible={showSearchResults}
      />
    </div>
  )
} 